'use client';

import { useEffect, useRef, useState } from 'react';
import { cn, isMobile, prefersReducedMotion } from '@/lib/utils';
import {
  activeLayerIndex,
  assemblyProgress,
  clamp01,
  easeOutCubic,
  layerProgress,
} from '@/lib/effects/stack';
import { useLocale } from '@/lib/i18n/LocaleContext';
import type { Dictionary } from '@/lib/i18n';

/**
 * "How we build" — Legora-style pinned glass stack.
 *
 * The section is several viewports tall; a sticky inner screen stays pinned
 * while scroll progress assembles five frosted-glass layers one per segment
 * (with a dwell on the finished stack for the last ~15% of the scroll). The
 * pill for the active layer expands into a description card.
 *
 * Two render paths for the visual:
 *   1. CSS 3D stack (default) — works with no asset, ships today.
 *   2. Scrubbed video — flip VIDEO_ENABLED once /videos/glass-stack.mp4 is
 *      generated (see docs/glass-stack-video.md). It lazy-loads as the
 *      section approaches and takes over; scroll drives video.currentTime
 *      exactly like legora.com. Skipped on touch / narrow / Save-Data
 *      clients where the CSS stack is cheaper than the all-intra download.
 *      NOTE: the video's assembly beats must match LAYERS.length segments.
 *
 * Performance: section geometry is measured once (and on resize) — zero
 * layout reads during the scrub. One rAF damped-follower loop runs only
 * while converging, gated by an IntersectionObserver "near" flag. Layer
 * transforms are set imperatively; React state changes only on segment
 * boundaries. Reduced motion renders the assembled stack with no scrubbing,
 * but keeps the active pill tracking scroll position discretely.
 */

// Capability-first copy, deliberately no framework or vendor names. The
// actual strings come from the dictionary (see `buildLayers` in the
// component); this constant only fixes how many layers there are, which is
// locale-independent and used by the scroll-scrub math below.
const LAYERS_COUNT = 5;

function buildLayers(dict: Dictionary) {
  return [
    dict.glassStack.layers.infrastructure,
    dict.glassStack.layers.ai,
    dict.glassStack.layers.web,
    dict.glassStack.layers.mobile,
    dict.glassStack.layers.design,
  ];
}

const LAYER_GAP = 34; // px of translateZ between stacked layers
const DROP_HEIGHT = 220; // px a layer hovers above its slot before assembling
const ASSEMBLY_END = 0.85; // assembly finishes here; rest of the scroll dwells
/** Damping factor for the scrub follower — progress eases toward the scroll
    target each frame instead of tracking it 1:1, so the motion glides. */
const DAMP = 0.14;
/** Flip to true when public/videos/glass-stack.mp4 ships — while false no
    request is made, so we don't 404 on every visit for an aspirational asset. */
const VIDEO_ENABLED = false;
const VIDEO_SRC = '/videos/glass-stack.mp4';

export function GlassStackSection() {
  const { dict } = useLocale();
  const layers = buildLayers(dict);
  const sectionRef = useRef<HTMLElement>(null);
  const layerRefs = useRef<Array<HTMLDivElement | null>>([]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const activeRef = useRef(0);
  const [active, setActive] = useState(0);
  const [videoOn, setVideoOn] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const reduced = prefersReducedMotion();

    // Geometry cache — rect reads during the scrub would be pure
    // layout-thrash. Measured at mount, on resize, on window load, and from
    // the IntersectionObserver's free rect on every approach: sections above
    // (portfolio pinning) resize themselves after mount, which would leave a
    // one-shot measurement stale and the scrub offset.
    let sectionTop = 0;
    let total = 1;
    const setGeometry = (rect: DOMRect) => {
      sectionTop = rect.top + window.scrollY;
      total = Math.max(1, rect.height - window.innerHeight);
    };
    const measure = () => setGeometry(section.getBoundingClientRect());
    measure();
    if (document.readyState !== 'complete') {
      window.addEventListener('load', measure, { once: true });
    }
    const progressNow = () => clamp01((window.scrollY - sectionTop) / total);

    let lastP = -1;
    let videoUsable = false;

    const setActiveIndex = (idx: number) => {
      if (idx !== activeRef.current) {
        activeRef.current = idx;
        setActive(idx);
      }
    };

    const applyFrame = (p: number) => {
      if (Math.abs(p - lastP) < 0.0001) return;
      lastP = p;

      const ap = assemblyProgress(p, ASSEMBLY_END);
      setActiveIndex(activeLayerIndex(ap, LAYERS_COUNT));

      layerRefs.current.forEach((el, i) => {
        if (!el) return;
        const lp = easeOutCubic(layerProgress(ap, i, LAYERS_COUNT));
        const lift = (1 - lp) * DROP_HEIGHT;
        // Slight twist + scale while descending so the landing feels settled,
        // not elevator-like. Unassembled layers stay softly visible so the
        // exploded state reads as a whole stack, not appearing parts.
        el.style.transform = `translateZ(${i * LAYER_GAP + lift}px) rotate(${(1 - lp) * 5}deg) scale(${0.95 + 0.05 * lp})`;
        el.style.opacity = String(0.35 + 0.65 * lp);
      });

      const video = videoRef.current;
      if (videoUsable && video && video.duration && video.readyState >= 2) {
        const target = Math.min(video.duration - 0.05, ap * video.duration);
        if (Math.abs(video.currentTime - target) > 1 / 60) {
          video.currentTime = target;
        }
      }
    };

    if (reduced) {
      // Assembled stack, no scrubbing — but the active pill still tracks
      // scroll discretely so the pills stay honest and clickable.
      const onScrollReduced = () => {
        setActiveIndex(
          activeLayerIndex(assemblyProgress(progressNow(), ASSEMBLY_END), LAYERS_COUNT),
        );
      };
      const onResizeReduced = () => {
        measure();
        onScrollReduced();
      };
      const raf = requestAnimationFrame(() => {
        applyFrame(1);
        onScrollReduced();
      });
      window.addEventListener('scroll', onScrollReduced, { passive: true });
      window.addEventListener('resize', onResizeReduced, { passive: true });
      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener('load', measure);
        window.removeEventListener('scroll', onScrollReduced);
        window.removeEventListener('resize', onResizeReduced);
      };
    }

    let near = false;
    let raf = 0;
    let looping = false;
    let target = 0;
    let smoothed = -1; // first onScroll snaps to target instead of animating from 0

    // Damped follower: runs only while converging (~0.5s after scroll input
    // stops), then goes fully idle — no persistent rAF.
    const startLoop = () => {
      if (looping) return;
      looping = true;
      const step = () => {
        const delta = target - smoothed;
        if (Math.abs(delta) < 0.0005) {
          smoothed = target;
          applyFrame(smoothed);
          looping = false;
          return;
        }
        smoothed += delta * DAMP;
        applyFrame(smoothed);
        raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    };

    const onScroll = () => {
      if (!near) return;
      target = progressNow();
      if (smoothed < 0) smoothed = target;
      startLoop();
    };
    const onResize = () => {
      measure();
      onScroll();
    };

    // The video is worth its (all-intra, large-ish) download only where the
    // scrub will actually be seen smoothly.
    const video = videoRef.current;
    const videoAllowed =
      VIDEO_ENABLED &&
      !!video &&
      !isMobile() &&
      window.innerWidth >= 768 &&
      !(navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;

    // When the video becomes seekable, sync it to the current progress before
    // revealing it — otherwise it would swap in showing frame 0 regardless of
    // where the user is in the section.
    const onCanPlay = () => {
      videoUsable = true;
      lastP = -1;
      applyFrame(smoothed < 0 ? 0 : smoothed);
      setVideoOn(true);
    };
    const onVideoError = () => {
      videoUsable = false;
      setVideoOn(false);
    };
    if (videoAllowed) {
      video.addEventListener('canplay', onCanPlay);
      video.addEventListener('error', onVideoError);
    }
    let videoAttached = false;

    // One observer gates both the scroll work and the video download.
    const observer =
      typeof IntersectionObserver !== 'undefined'
        ? new IntersectionObserver(
            (entries) => {
              near = entries.some((e) => e.isIntersecting);
              if (!near) return;
              // Refresh geometry from the observer's rect — layout above the
              // section may have shifted since mount, and this rect is free.
              const entry = entries[entries.length - 1];
              setGeometry(entry.boundingClientRect);
              onScroll();
              if (videoAllowed && !videoAttached) {
                videoAttached = true;
                // preload="none" keeps the idle element inert, but once we
                // decide to load, buffering must actually start or canplay
                // never fires and the scrub would deadlock on the CSS stack.
                video.preload = 'auto';
                video.src = VIDEO_SRC;
                video.load();
              }
            },
            { rootMargin: '100% 0%' },
          )
        : undefined;
    if (observer) {
      observer.observe(section);
    } else {
      near = true;
    }

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    return () => {
      window.removeEventListener('load', measure);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (videoAllowed) {
        video.removeEventListener('canplay', onCanPlay);
        video.removeEventListener('error', onVideoError);
      }
      cancelAnimationFrame(raf);
      observer?.disconnect();
    };
  }, []);

  const jumpToSegment = (i: number) => {
    const section = sectionRef.current;
    if (!section) return;
    const top = section.getBoundingClientRect().top + window.scrollY;
    const total = section.offsetHeight - window.innerHeight;
    window.scrollTo({
      top: top + ((i + 0.55) / LAYERS_COUNT) * total * ASSEMBLY_END,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  return (
    <section
      ref={sectionRef}
      id="how-we-build"
      className="light-section self-styled relative z-20"
      // Slightly translucent cream: the ambient wing (ink mode over light
      // sections) can faintly show through without breaking the paper look.
      style={{ height: '420vh', backgroundColor: 'rgba(242,241,238,0.92)' }}
    >
      <div
        className="sticky top-0 flex h-screen w-full flex-col overflow-hidden pt-20 md:block md:pt-0"
        style={{ height: '100dvh' }}
      >
        {/* Eyebrow (decorative — the real heading is visually hidden) */}
        <h2 className="sr-only">{dict.glassStack.sectionHeading}</h2>
        <div className="relative z-20 px-6 md:absolute md:start-20 md:top-28 md:px-0">
          <span className="eyebrow eyebrow--ink" aria-hidden="true">
            {dict.glassStack.eyebrow}
          </span>
        </div>

        {/* Layer pills — active one expands into a description card */}
        <div className="relative z-20 mt-3 flex w-full flex-col gap-1.5 px-6 md:absolute md:start-20 md:top-1/2 md:mt-0 md:w-[340px] md:-translate-y-1/2 md:gap-2 md:px-0">
          {layers.map((layer, i) => {
            const isActive = active === i;
            return (
              <button
                key={layer.title}
                type="button"
                onClick={() => jumpToSegment(i)}
                aria-pressed={isActive}
                className={cn(
                  // One border-radius for both states — the pill and the card
                  // share it, so the expand/collapse morph is seamless (the
                  // radius never jumps; only padding/colors animate).
                  'w-fit max-w-full cursor-pointer rounded-[26px] text-start transition-[background-color,padding,box-shadow,border-color] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]',
                  isActive
                    ? 'border border-transparent bg-[#2d2d2d] px-4 py-3 text-white shadow-lg md:w-full md:px-5 md:py-4'
                    : 'border border-black/15 bg-white/70 px-3.5 py-1.5 text-black/70 hover:bg-white md:px-4 md:py-2',
                )}
              >
                <span className="flex items-center justify-between gap-3">
                  <span
                    className={cn(
                      'rtl-label font-mono text-[11px] uppercase tracking-[0.08em] md:text-[12px]',
                      isActive ? 'text-white/70' : 'text-black/70',
                    )}
                  >
                    {layer.title}
                  </span>
                  {!isActive && (
                    <span
                      aria-hidden="true"
                      className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-black/20 text-[10px] leading-none text-black/50"
                    >
                      +
                    </span>
                  )}
                </span>
                <span
                  className="grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]"
                  style={{ gridTemplateRows: isActive ? '1fr' : '0fr' }}
                >
                  <span className="block overflow-hidden">
                    <span className="rtl-body block pt-2 text-[12px] font-light leading-relaxed text-white/85 md:text-[13px]">
                      {layer.desc}
                    </span>
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {/* Visual — CSS glass stack, replaced by the scrubbed video when
            present. Both live in the same square container, so their
            placement can never drift apart. On md+ the centering pads past
            the pills column so the stack never slides underneath it on
            tablet widths. */}
        <div className="relative z-10 flex min-h-0 flex-1 items-center justify-center md:absolute md:inset-0 md:ps-[400px]">
          <div
            className="relative aspect-square w-[min(72vw,36vh)] md:w-[min(42vw,58vh,560px)]"
            style={{ perspective: '1500px' }}
            aria-hidden="true"
          >
            <div
              className={cn('absolute inset-0', videoOn && 'invisible')}
              style={{ transformStyle: 'preserve-3d', transform: 'rotateX(58deg) rotateZ(45deg)' }}
            >
              {/* Ground shadow */}
              <div
                className="absolute inset-[8%] rounded-[36px]"
                style={{
                  background: 'radial-gradient(closest-side, rgba(20,30,28,0.3), transparent 72%)',
                  transform: 'translateZ(-46px) scale(1.08)',
                  filter: 'blur(22px)',
                }}
              />
              {layers.map((layer, i) => (
                <div
                  key={layer.title}
                  ref={(el) => {
                    layerRefs.current[i] = el;
                  }}
                  className="absolute inset-[10%] rounded-[30px]"
                  style={{
                    zIndex: i,
                    background:
                      'linear-gradient(135deg, rgba(255,255,255,0.94) 0%, rgba(241,244,245,0.55) 45%, rgba(226,231,233,0.8) 100%)',
                    border: '1px solid rgba(255,255,255,0.95)',
                    boxShadow:
                      '0 30px 60px -22px rgba(30,45,42,0.3), inset 0 1px 0 rgba(255,255,255,0.95), inset 0 -16px 30px rgba(178,192,196,0.35)',
                    transform: `translateZ(${i * LAYER_GAP}px)`,
                  }}
                >
                  {/* Etched label on the glass — always readable, brightest
                      while its layer is active */}
                  <span
                    className="rtl-etch absolute inset-0 flex items-center justify-center font-mono text-[11px] uppercase tracking-[0.24em] text-black transition-opacity duration-500 md:text-[13px]"
                    style={{ opacity: active === i ? 0.6 : 0.28 }}
                  >
                    {layer.title}
                  </span>
                </div>
              ))}
            </div>

            <video
              ref={videoRef}
              muted
              playsInline
              preload="none"
              className={cn(
                'pointer-events-none absolute inset-0 h-full w-full object-contain',
                !videoOn && 'hidden',
              )}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
